import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fmtMXN } from "@/lib/format";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal, itemKey } = useCart();
  const navigate = useNavigate();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-arre-black border-l border-arre-border flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between p-6 border-b border-arre-border">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-white/50">Tu pedido</div>
            <h3 className="font-display text-3xl text-white tracking-wider uppercase">La Canasta</h3>
          </div>
          <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="cart-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <ShoppingBag className="w-16 h-16 text-white/20 mb-4" strokeWidth={1} />
            <p className="font-display text-2xl text-white tracking-wider uppercase mb-2">
              Sin nada todavía, compare
            </p>
            <p className="text-white/50 text-sm mb-8">
              Agrega unos cortes o un paquetazo para empezar.
            </p>
            <button
              onClick={() => {
                setOpen(false);
                navigate("/paquetes");
              }}
              className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase px-8 py-3"
              data-testid="cart-empty-cta"
            >
              Ver paquetes
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {items.map((it) => {
                const k = itemKey(it);
                return (
                  <div
                    key={k}
                    className="flex gap-4 pb-5 border-b border-arre-border"
                    data-testid={`cart-item-${it.id}-${it.variant_id || "default"}`}
                  >
                    <div
                      className="w-20 h-24 bg-arre-surface bg-cover bg-center flex-shrink-0"
                      style={{ backgroundImage: `url(${it.image})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">
                            {it.type === "package" ? "Paquete" : "Corte"}
                          </div>
                          <p className="font-display text-xl text-white tracking-wider leading-tight">
                            {it.name}
                          </p>
                          {it.unit && <p className="text-xs text-white/50">{it.unit}</p>}
                        </div>
                        <button
                          onClick={() => removeItem(k)}
                          className="text-white/40 hover:text-arre-red"
                          data-testid={`cart-remove-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-arre-border">
                          <button
                            onClick={() => updateQty(k, it.quantity - 1)}
                            className="px-3 py-1 text-white hover:bg-arre-surface"
                            data-testid={`cart-minus-${it.id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="px-4 text-white font-display text-lg" data-testid={`cart-qty-${it.id}`}>
                            {it.quantity}
                          </span>
                          <button
                            onClick={() => updateQty(k, it.quantity + 1)}
                            className="px-3 py-1 text-white hover:bg-arre-surface"
                            data-testid={`cart-plus-${it.id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-white font-display text-xl tracking-wider">
                          {fmtMXN(it.price * it.quantity)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-arre-border p-6 space-y-4 bg-arre-surface/50">
              <div className="flex items-center justify-between">
                <span className="text-xs tracking-[0.2em] uppercase text-white/50">Subtotal</span>
                <span className="font-display text-3xl text-white tracking-wider" data-testid="cart-subtotal">
                  {fmtMXN(subtotal)}
                </span>
              </div>
              <p className="text-[11px] text-white/40">El envío se calcula en el checkout según tu CP.</p>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/checkout");
                }}
                className="w-full btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase py-4"
                data-testid="cart-checkout-btn"
              >
                Pagar ahora →
              </button>
              <Link
                to="/paquetes"
                onClick={() => setOpen(false)}
                className="block text-center text-white/60 hover:text-white text-xs tracking-[0.2em] uppercase"
              >
                Seguir comprando
              </Link>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
